// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  loginUser, // Giữ nguyên tên import từ service
  getUserByToken,
  updateUser,
  changePassword,
} from '../services/authService';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Khởi tạo là true, vì sẽ chạy checkAuth khi mount
  const [authError, setAuthError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('jwtToken')); // Lấy token từ localStorage

  const checkAuthInternal = useCallback(async () => {
    const currentTokenFromState = token; 

    if (!currentTokenFromState) {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false); // Hoàn tất quá trình kiểm tra
      return;
    }

    setIsLoading(true); // Bắt đầu kiểm tra/xác thực
    setAuthError(null); 
    try {
      // 2. getUserByToken không cần tham số token vì interceptor xử lý
      const userData = await getUserByToken(); 
      setIsAuthenticated(true);
      setUser({
        id: userData.id,
        email: userData.email,
        name: userData.fullName,
        address: userData.address,
        phone: userData.phone,
        role: userData.role?.name || userData.role, // Xử lý role object/string
      });
      // Không cần setToken(currentTokenFromState) vì token đã có trong state
    } catch (error) {
      console.error('CheckAuth error:', error);
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('jwtToken'); // Xóa token không hợp lệ
      setToken(null); // Cập nhật state `token` thành null. Điều này sẽ làm useEffect chạy lại.
      setAuthError(error.message || 'Phiên đăng nhập không hợp lệ hoặc đã hết hạn.');
    } finally {
      setIsLoading(false); // Luôn kết thúc loading
    }
  }, [token]); // `checkAuthInternal` được tạo lại khi `token` state thay đổi

  // 4. useEffect sẽ chạy khi mount và mỗi khi `checkAuthInternal` (do `token` thay đổi) được tạo lại.
  useEffect(() => {
    // console.log("AuthContext useEffect running due to checkAuthInternal/token change. Current token state:", token);
    checkAuthInternal();
  }, [checkAuthInternal]);

  const login = async (credentials) => {
    setIsLoading(true);
    setAuthError(null);
    try {
        const { user: userData, token: receivedToken } = await loginUser(credentials);
        localStorage.setItem('jwtToken', receivedToken);
        setToken(receivedToken);

        // Debug: Kiểm tra token ngay sau khi lưu
        console.log('Login: Token saved to localStorage:', receivedToken);
        try {
            const testUserData = await getUserByToken();
            console.log('Login: Test getUserByToken successful:', testUserData);
        } catch (testError) {
            console.error('Login: Test getUserByToken failed:', testError);
            throw new Error('Token không hợp lệ ngay sau khi đăng nhập.');
        }

        setUser({
            id: userData.id,
            email: userData.email,
            name: userData.fullName,
            address: userData.address,
            phone: userData.phone,
            role: userData.role?.name || userData.role,
        });
        setIsAuthenticated(true);
        return userData;
    } catch (error) {
        console.error('Login error:', error);
        setAuthError(error.message || 'Đăng nhập thất bại.');
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        localStorage.removeItem('jwtToken');
        throw error;
    } finally {
        setIsLoading(false);
    }
};

  const updateUserInfo = async (newUserData) => {
    if (!user || !user.id) {
      throw new Error("Không có thông tin người dùng để cập nhật.");
    }
    try {
      // updateUser trong authService không cần user.id nếu backend lấy từ token
      const updatedUser = await updateUser(newUserData); 
      setUser({ // Cập nhật user state từ response
        id: updatedUser.id,
        email: updatedUser.email,
        name: updatedUser.name, // Giả sử API trả về name
        address: updatedUser.address,
        phone: updatedUser.phone,
        role: updatedUser.role?.name || updatedUser.role,
      });
      // 6. Cân nhắc việc gọi checkAuthInternal()
      // await checkAuthInternal(); // Nếu muốn đồng bộ lại hoàn toàn với server, nhưng sẽ có thêm lượt loading
      return updatedUser;
    } catch (error) {
      console.error("Update user error:", error);
      setAuthError(error.message || 'Cập nhật thông tin thất bại.');
      throw error;
    } finally {
      // setIsLoading(false);
    }
  };
  
  // Đổi tên hàm để nhất quán hoặc sửa tên khi import
  const changeUserPassword = async (passwordData) => {
    if (!user || !user.id) {
      throw new Error("Không có thông tin người dùng để đổi mật khẩu.");
    }
    // setIsLoading(true);
    try {
      // changePassword trong authService không cần user.id
      await changePassword(passwordData);
    } catch (error) {
      console.error("Change password error:", error);
      setAuthError(error.message || 'Đổi mật khẩu thất bại.');
      throw error;
    } finally {
      // setIsLoading(false);
    }
  };

  const logout = () => {
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      setAuthError(null);
      localStorage.removeItem('jwtToken');
  };
  useEffect(() => {
    const handleStorageChange = (e) => {
        if (e.key === 'jwtToken') {
            setToken(e.newValue);
            if (!e.newValue) {
                setIsAuthenticated(false);
                setUser(null);
                setAuthError('Phiên đăng nhập đã bị hủy từ tab khác.');
            }
        }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
}, []);
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token, // 7. Cung cấp token state ra ngoài
        login,
        logout,
        isLoading, // `isLoading` này phản ánh trạng thái của checkAuthInternal hoặc login
        // checkAuth: checkAuthInternal, // Expose nếu cần gọi từ bên ngoài, nhưng thường không cần
        authError,
        updateUserInfo,
        changeUserPassword, // Đổi tên ở đây nếu cần
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};